import type { WorkbookProfile } from '../../types/database.ts';
import type { ProgressionZone } from '../level-progression.ts';
import type { CefrLevelCode } from '../../constants/languages.ts';
import { getExampleBlock } from '../example-data.ts';

/**
 * Context passed to every block prompt builder.
 */
export interface BlockPromptContext {
  /** The full workbook profile with all personalization data. */
  profile: WorkbookProfile;

  /** The overarching workbook theme (e.g., "Reizen & Gezondheid"). */
  workbookTheme: string;

  /** The specific sub-theme assigned to this block by the composition engine. */
  subTheme: string;

  /** The block's position in the workbook (1-based). */
  blockOrder: number;

  /** Total number of blocks in this workbook. */
  totalBlocks: number;

  /**
   * Vocabulary words already introduced in earlier blocks.
   * Used to encourage ~30% overlap and prevent complete repetition.
   * Each entry is a "target — native" pair string.
   */
  previousVocabulary: string[];

  /**
   * Summaries of previously generated blocks for coherence.
   * Each entry: "Block N: <type> — <title> (<sub-theme>)"
   */
  previousBlockSummaries: string[];

  /**
   * Present only for complete (progressive) workbooks.
   * Contains the zone, level, and instructions for this block's
   * position in the CEFR progression.
   */
  progression?: {
    zone: ProgressionZone;
    primaryLevel: CefrLevelCode;
    endLevel: CefrLevelCode;
    progressionNote: string;
  };

  /**
   * Present only for complete workbooks. Identifies which chapter
   * this block belongs to.
   */
  chapter?: {
    number: number;
    title: string;
  };

  // ── Cross-month deduplication (subscription renewals) ──

  /**
   * Vocabulary pairs from all previous months' workbooks.
   * Used to avoid repeating the same items across subscription months.
   */
  previousMonthsVocabulary?: string[];

  /**
   * Summaries of blocks from previous months for scenario deduplication.
   * Each entry: "Block N: <type> — <title> (<sub-theme>)"
   */
  previousMonthsSummaries?: string[];

  /**
   * Verb infinitives from previous months' workbooks.
   * Used by conjugation spotlight to pick different verbs.
   */
  previousMonthsVerbs?: string[];

  /**
   * Reading themes from previous months: "textType: title" strings.
   * Used by reading comprehension to pick different formats/topics.
   */
  previousMonthsReadingThemes?: string[];

  /**
   * Which month this workbook is (1-based). Month 1 = first workbook.
   * Absent or 1 means no cross-month context.
   */
  workbookNumber?: number;

  /**
   * When true, the generation is for a free preview. Prompt builders
   * use this to strengthen personalization wording so the preview
   * showcases the "wow" factor even with fewer blocks.
   */
  isPreview?: boolean;
}

/**
 * Context for back-matter prompt builders.
 */
export interface BackMatterPromptContext {
  profile: WorkbookProfile;
  workbookTheme: string;

  /**
   * All target-language verbs extracted from generated blocks.
   * Used by the verb glossary builder.
   */
  extractedVerbs: string[];

  /** Block summaries for reference. */
  blockSummaries: string[];

  /**
   * Verb infinitives from previous months' workbooks.
   * Used by the verb glossary builder to avoid repeating the same verbs.
   */
  previousMonthsVerbs?: string[];
}

/**
 * A prompt builder function. Takes context, returns the user-prompt string
 * to send to Claude (alongside the system prompt).
 */
export type BlockPromptBuilder = (ctx: BlockPromptContext) => string;
export type BackMatterPromptBuilder = (ctx: BackMatterPromptContext) => string;

// =============================================================================
// Progression clause — appended to each block prompt in progressive workbooks
// =============================================================================

/**
 * Returns a "## Level Progression" section to append to the user prompt.
 * For non-progressive workbooks (no `ctx.progression`), returns an empty string.
 *
 * This keeps progression logic out of individual prompt builders — they just
 * call `buildProgressionClause(ctx)` and append the result.
 */
export function buildProgressionClause(ctx: BlockPromptContext): string {
  if (!ctx.progression) return '';

  // Import the CEFR guidelines for the primary level of this block
  // (the guidelines are already embedded in the module scope via the
  // system-prompt.ts export, but we inline a summary here to keep
  // the prompt self-contained per block)
  const { zone, primaryLevel, endLevel, progressionNote } = ctx.progression;
  const chapterInfo = ctx.chapter ? `Chapter ${ctx.chapter.number}: "${ctx.chapter.title}"\n` : '';

  return `

## Level Progression
${chapterInfo}Zone: **${zone}** | Primary level: **${primaryLevel.toUpperCase()}** | Workbook progression: ${ctx.profile.level.toUpperCase()} → ${endLevel.toUpperCase()}

${progressionNote}

**IMPORTANT:** Use ${primaryLevel.toUpperCase()}-appropriate vocabulary and grammar for this block. The level specified here overrides any fixed level in the system prompt.`;
}

/**
 * Returns a "## Reference Example" section for progressive workbooks.
 * If no example exists for this zone+blockType combo, returns an empty string.
 *
 * Few-shot examples help Claude match the expected format and difficulty level
 * for the complete tier's progression zones.
 */
export function buildExampleClause(ctx: BlockPromptContext, blockType: string): string {
  if (!ctx.progression) return '';

  const example = getExampleBlock(ctx.progression.zone, blockType);
  if (!example) return '';

  return `

## Reference Example
Here is an example of a well-formed ${blockType.replace(/_/g, ' ')} block at the **${ctx.progression.zone}** level. Use it as a format and quality reference — adapt the content to your specific theme and language pair.

\`\`\`json
${example}
\`\`\``;
}

// =============================================================================
// Cross-month clause — appended to each block prompt in subscription renewals
// =============================================================================

/**
 * Returns a "## Cross-Month History" section to append to the user prompt.
 * For month 1 (or when no cross-month context is present), returns an empty string.
 *
 * This keeps deduplication logic out of individual prompt builders — they just
 * call `buildCrossMonthClause(ctx)` and append the result.
 */
export function buildCrossMonthClause(ctx: BlockPromptContext): string {
  if (!ctx.workbookNumber || ctx.workbookNumber <= 1) return '';

  const sections: string[] = [];

  sections.push(`This is **Month ${ctx.workbookNumber}** of the learner's subscription.`);

  if (ctx.previousMonthsVocabulary && ctx.previousMonthsVocabulary.length > 0) {
    sections.push(`### Previously Covered Vocabulary (from months 1–${ctx.workbookNumber - 1})

${ctx.previousMonthsVocabulary.map((v) => `- ${v}`).join('\n')}

**Rule:** Do NOT introduce these as new vocabulary items. You may reuse up to 30% of them for reinforcement in example sentences or context, but the primary vocabulary in this block must be fresh.`);
  }

  if (ctx.previousMonthsSummaries && ctx.previousMonthsSummaries.length > 0) {
    sections.push(`### Previous Exercise Summaries

${ctx.previousMonthsSummaries.map((s) => `- ${s}`).join('\n')}

**Rule:** Create completely different scenarios, situations, and exercise angles. Do not repeat the same themes, storylines, or setups from previous months.`);
  }

  return `

## Cross-Month History
${sections.join('\n\n')}`;
}
